export const OS = {
  LINUX: 'linux',
  WIN: 'win',
  MACOS: 'macos',
  IOS: 'ios',
  ANDROID: 'android',
  UNKNOWN: 'unknown',
} as const;

type EmptyObject = Record<string, never>;

export const getWindowObj = (): Window | EmptyObject =>
  typeof window !== 'undefined' ? window : {};

export const getNavigator = (): Navigator | EmptyObject => getWindowObj().navigator ?? {};

export const getOS = (navigator: Navigator | EmptyObject): string => {
  const android = /android/i.test(navigator.userAgent || '');
  const linux = /linux/i.test(navigator.platform || '');
  const mac = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.platform || '');
  const ios =
    /ipad|iphone|ipod/i.test(navigator.userAgent || '') ||
    (mac && (navigator.maxTouchPoints || 0) > 0);
  const win = /Win64|Win32|Windows|WinCE/i.test(navigator.platform || '');

  switch (true) {
    case ios:
      return OS.IOS;
    case android:
      return OS.ANDROID;
    case linux:
      return OS.LINUX;
    case mac:
      return OS.MACOS;
    case win:
      return OS.WIN;
    default:
      return OS.UNKNOWN;
  }
};

export const OS_INFO = getOS(getNavigator());

export const isMobile = OS_INFO === OS.IOS || OS_INFO === OS.ANDROID;

export const Client = {
    CHROME: 'chrome',
    FF: 'firefox',
    SAFARI: 'safari',
    OPERA: 'opera',
    EDGE: 'edge',
    EDGE_CHROMIUM: 'edge_chromium',
    SAMSUNG: 'samsung',
    IE: 'ie',
    APPLICATION: 'application',
} as const;

type ClientInfo = {
    name: string | undefined;
    version?: string;
    chromeVersion?: string;
};

type IntervalVersion = null | undefined | string | number;
type Interval = Array<IntervalVersion>;

const fakeVer = ['0', '0'];

const getChromeVersion = (navigator: Navigator | EmptyObject): string => {
    const matchedVersion = (navigator.appVersion || '').match(/(?:(Chrome|CriOS)\/)(\d+(\.\d+)*)/);
    return matchedVersion ? matchedVersion[2] : fakeVer[1];
};

export const getClient = (navigator: Navigator | EmptyObject): ClientInfo => {
    switch (navigator.vendor) {
        case 'Google Inc.':
            switch (true) {
                case /Opera|OPR\//.test(navigator.userAgent || ''):
                    return { name: Client.OPERA, version: fakeVer[1] };
                case /Edg/.test(navigator.userAgent || ''):
                    return { name: Client.EDGE_CHROMIUM, version: getChromeVersion(navigator) };
                case /SamsungBrowser/i.test(navigator.userAgent || ''):
                    return { name: Client.SAMSUNG, version: fakeVer[1] };
                default:
                    return { name: Client.CHROME, version: getChromeVersion(navigator) };
            }

        case 'Apple Computer, Inc.':
            if (/CriOS/i.test(navigator.userAgent || '')) {
                return { name: Client.CHROME, version: getChromeVersion(navigator) };
            }

            return {
                name: Client.SAFARI,
                version: ((navigator.appVersion || '').match(/(?:Version\/)(\d+(\.\d+)*)/) || fakeVer)[1],
            };
        case 'Microsoft':
        case '':
            if (typeof window === 'object' && 'ActiveXObject' in window) {
                return { name: Client.IE, version: fakeVer[1] };
            }

            if (/Edge/.test(navigator.userAgent || '')) {
                return { name: Client.EDGE, version: fakeVer[1] };
            }

            return {
                name: Client.FF,
                version: ((navigator.userAgent || '').match(/(?:Firefox\/)(\d+(\.\d+)*)/) || fakeVer)[1],
            };
    }

    return { name: undefined };
};

export const UA_INFO = getClient(getNavigator());

const getNumericVersion = (v: string | undefined, defaultV = 0): number =>
    typeof v === 'string' && v.length > 0 ? Number(v) : defaultV;

export const compareVersions = (
    v1: IntervalVersion,
    v2: IntervalVersion,
    fillEmptyV1 = 0,
    fillEmptyV2 = 0
): number => {
    const splittedV1 = ('' + v1).split('.');
    const splittedV2 = ('' + v2).split('.');
    const maxLength = Math.max(splittedV1.length, splittedV2.length);

    for (let i = 0; i < maxLength; i++) {
        const v1Subversion = getNumericVersion(splittedV1[i], fillEmptyV1);
        const v2Subversion = getNumericVersion(splittedV2[i], fillEmptyV2);

        if (v1Subversion > v2Subversion) {
            return 1;
        }

        if (v1Subversion < v2Subversion) {
            return -1;
        }
    }

    return 0;
};

export const checkIsVersionInInterval = (v: IntervalVersion, [vFrom, vUpTo]: Interval): boolean =>
    (vFrom === null || vFrom === undefined || compareVersions(v, vFrom) >= 0) &&
    (vUpTo === null || vUpTo === undefined || compareVersions(v, vUpTo, 0, Infinity) <= 0);

export const isVersionInIntervals = (
    v: number | string | undefined,
    intervals: Array<Interval> = []
): boolean => intervals.some(interval => checkIsVersionInInterval(v, interval));

export const checkClient =
    (hypothesisName: string, actual: ClientInfo) =>
    (intervals?: Array<Interval>): boolean =>
        hypothesisName === actual.name &&
        (!intervals || isVersionInIntervals(actual.version, intervals));

export const isMac = OS_INFO === OS.MACOS;
export const isWin = OS_INFO === OS.WIN;
export const isIOS = OS_INFO === OS.IOS;
export const isAndroid = OS_INFO === OS.ANDROID;

export const isElectron = (): boolean => {
    if (
        typeof window !== 'undefined' &&
        typeof window.process === 'object' &&
        (window.process as { type?: string }).type === 'renderer'
    ) {
        return true;
    }

    const navigator = getNavigator();

    return Boolean(navigator.userAgent?.indexOf?.('Electron') >= 0);
};

export const isChrome = checkClient(Client.CHROME, UA_INFO);
export const isFF = checkClient(Client.FF, UA_INFO);
export const isSafari = checkClient(Client.SAFARI, UA_INFO);
export const isEdge = checkClient(Client.EDGE, UA_INFO);
export const isEdgeChromium = checkClient(Client.EDGE_CHROMIUM, UA_INFO);
export const isApplication = checkClient(Client.APPLICATION, UA_INFO);
export const isDesktopSafari = !isMobile && isSafari();
